import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';

import EventEmitter from './EventEmitter';
import { AudioLoader, ObjectLoader, TextureLoader } from 'three';
import HttpLoader from '../Components/HttpLoader';
import AxiosHttpClient from '../Network/AxiosHttpClient';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';

export default class Loader extends EventEmitter {
    public toLoad: number;
    public loaded: number;
    public loaders: { extensions: string[]; action: (resource: any) => void }[];
    public items: { [key: string]: any } = {};

    public constructor(public httpClient: AxiosHttpClient) {
        super();

        this.setLoaders();

        this.toLoad = 0; // total resources
        this.loaded = 0; // loaded resources
    }

    public setLoaders() {
        const textureLoader = new TextureLoader();
        this.loaders = [];

        // ex: _resource: { name: 'matcapRed', source: 'xxx.jpg' }
        this.loaders.push({
            extensions: ['jpg', 'png', 'jpeg'],
            action: (_resource) => {
                textureLoader.load(_resource.source, (_texture) => {
                    this.fileLoadEnd(_resource, _texture);
                });
            },
        });

        //Audio
        const audioLoader = new AudioLoader();
        this.loaders.push({
            extensions: ['mp3', 'ogg'],
            action: (_resource) => {
                audioLoader.load(_resource.source, (_audio) => {
                    this.fileLoadEnd(_resource, _audio);
                });
            },
        });

        // Draco
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath('/draco/');
        dracoLoader.setDecoderConfig({ type: 'js' });

        this.loaders.push({
            extensions: ['drc'],
            action: (_resource) => {
                dracoLoader.load(_resource.source, (_data) => {
                    this.fileLoadEnd(_resource, _data);
                    // this is deprecated https://discourse.threejs.org/t/issue-with-setdecoderpath/9556
                    (DRACOLoader as any).releaseDecoderModule();
                });
            },
        });

        const svgLoader = new SVGLoader();
        this.loaders.push({
            extensions: ['svg'],
            action: (_resource) => {
                svgLoader.load(_resource.source, (_data) => {
                    this.fileLoadEnd(_resource, _data);
                });
            },
        });

        // GLTF
        const gltfLoader = new GLTFLoader();
        gltfLoader.setDRACOLoader(dracoLoader);

        this.loaders.push({
            extensions: ['glb', 'gltf'],
            action: (_resource) => {
                gltfLoader.load(_resource.source, (_data) => {
                    this.fileLoadEnd(_resource, _data);
                });
            },
        });

        // FBX
        const fbxLoader = new FBXLoader();

        this.loaders.push({
            extensions: ['fbx'],
            action: (_resource) => {
                fbxLoader.load(_resource.source, (_data) => {
                    this.fileLoadEnd(_resource, _data);
                });
            },
        });

        const objectLoader = new ObjectLoader();
        const fontLoader = new FontLoader();

        this.loaders.push({
            extensions: ['json'],
            action: (_resource) => {
                if (_resource.type === 'font') {
                    fontLoader.load(_resource.source, (_data) => {
                        this.fileLoadEnd(_resource, _data);
                    });
                } else {
                    objectLoader.load(_resource.source, (_data) => {
                        this.fileLoadEnd(_resource, _data);
                    });
                }
            },
        });
    }

    // execute after loading each asset
    public fileLoadEnd(_resource: any, _data: any) {
        this.loaded += 1;
        this.items[_resource.name] = _data;

        this.trigger('fileEnd', [_resource, _data]);

        if (this.loaded === this.toLoad) {
            this.trigger('end');
        }
    }

    private loadSingleResource(_resource: any) {
        if (_resource.type === 'http') {
            if (_resource.dependsOn) {
                const dependency = this.items[_resource.dependsOn.resource];
                if (dependency) {
                    const resolvedSource = _resource.source(dependency);
                    const httpLoader = new HttpLoader(this.httpClient);
                    httpLoader.load(resolvedSource, (data) => {
                        this.fileLoadEnd(_resource, data);
                    });
                    this.toLoad += 1;
                }
            } else {
                const httpLoader = new HttpLoader(this.httpClient);
                httpLoader.load(_resource.source, (data) => {
                    this.fileLoadEnd(_resource, data);
                });
                this.toLoad += 1;
            }
        } else {
            const extensionMatch = _resource.source.match(/\.([a-z]+)$/);

            if (typeof extensionMatch?.[1] !== 'undefined') {
                const extension = extensionMatch[1];
                const loader = this.loaders.find((_loader) =>
                    _loader.extensions.find(
                        (_extension) => _extension === extension,
                    ),
                );

                if (loader) {
                    if (_resource.dependsOn) {
                        const dependency =
                            this.items[_resource.dependsOn.resource];
                        if (dependency) {
                            const resolvedSource =
                                typeof _resource.source === 'function'
                                    ? _resource.source(dependency)
                                    : _resource.source;

                            loader.action({
                                ..._resource,
                                source: resolvedSource,
                            });
                            this.toLoad += 1;
                        }
                    } else {
                        loader.action(_resource);
                        this.toLoad += 1;
                    }
                } else {
                    console.warn(`Cannot found loader for ${_resource}`);
                }
            } else {
                if (_resource.type === 'font' || _resource.type === 'cube') {
                    const loader = this.loaders.find((_loader) =>
                        _loader.extensions.find(
                            (_extension) => _extension === 'json',
                        ),
                    );

                    if (loader) {
                        if (_resource.dependsOn) {
                            const dependency =
                                this.items[_resource.dependsOn.resource];
                            if (dependency) {
                                const resolvedSource =
                                    typeof _resource.source === 'function'
                                        ? _resource.source(dependency)
                                        : _resource.source;

                                loader.action({
                                    ..._resource,
                                    source: resolvedSource,
                                });
                                this.toLoad += 1;
                            }
                        } else {
                            loader.action(_resource);
                            this.toLoad += 1;
                        }
                    }
                } else {
                    console.warn(`Cannot found extension of ${_resource}`);
                }
            }
        }
    }

    // Asynchroniczna wersja ładowania pojedynczego zasobu
    private async loadSingleResourceAsync(_resource: any): Promise<any> {
        return new Promise((resolve, reject) => {
            if (_resource.type === 'http') {
                const httpLoader = new HttpLoader(this.httpClient);
                const source = _resource.dependsOn
                    ? _resource.source(this.items[_resource.dependsOn.resource])
                    : _resource.source;

                httpLoader.load(source, (data) => {
                    // Użyj fileLoadEnd() żeby prawidłowo zaktualizować liczniki
                    this.fileLoadEnd(_resource, data);
                    resolve(data);
                }, (error) => {
                    reject(new Error(`Failed to load HTTP resource ${_resource.name}: ${error}`));
                });
            } else {
                const extensionMatch = _resource.source.match(/\.([a-z]+)$/);
                let extension = extensionMatch?.[1];

                // Obsługa specjalnych typów bez rozszerzenia
                if (!extension && (_resource.type === 'font' || _resource.type === 'cube')) {
                    extension = 'json';
                }

                if (extension) {
                    const loaderConfig = this.loaders.find((_loader) =>
                        _loader.extensions.includes(extension),
                    );

                    if (loaderConfig) {
                        // Przygotowanie źródła
                        const source = _resource.dependsOn
                            ? (typeof _resource.source === 'function'
                                ? _resource.source(this.items[_resource.dependsOn.resource])
                                : _resource.source)
                            : _resource.source;

                        // Tworzenie wrappera dla różnych typów loaderów
                        this.createAsyncLoader(_resource, { ..._resource, source }, extension)
                            .then((data) => {
                                // Użyj fileLoadEnd() żeby prawidłowo zaktualizować liczniki
                                this.fileLoadEnd(_resource, data);
                                resolve(data);
                            })
                            .catch(reject);
                    } else {
                        reject(new Error(`Cannot find loader for extension: ${extension}`));
                    }
                } else {
                    reject(new Error(`Cannot determine extension for resource: ${_resource.name}`));
                }
            }
        });
    }

    // Helper do tworzenia async wrapperów dla różnych loaderów
    private createAsyncLoader(originalResource: any, resource: any, extension: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const onLoad = (data: any) => resolve(data);
            const onError = (error: any) => reject(error);

            switch (extension) {
                case 'jpg':
                case 'png':
                case 'jpeg':
                    const textureLoader = new TextureLoader();
                    textureLoader.load(resource.source, onLoad, undefined, onError);
                    break;

                case 'mp3':
                case 'ogg':
                    const audioLoader = new AudioLoader();
                    audioLoader.load(resource.source, onLoad, undefined, onError);
                    break;

                case 'drc':
                    const dracoLoader = new DRACOLoader();
                    dracoLoader.setDecoderPath('/draco/');
                    dracoLoader.setDecoderConfig({ type: 'js' });
                    dracoLoader.load(resource.source, (data) => {
                        (DRACOLoader as any).releaseDecoderModule();
                        onLoad(data);
                    }, undefined, onError);
                    break;

                case 'svg':
                    const svgLoader = new SVGLoader();
                    svgLoader.load(resource.source, onLoad, undefined, onError);
                    break;

                case 'glb':
                case 'gltf':
                    const gltfLoader = new GLTFLoader();
                    const dracoLoaderForGLTF = new DRACOLoader();
                    dracoLoaderForGLTF.setDecoderPath('/draco/');
                    dracoLoaderForGLTF.setDecoderConfig({ type: 'js' });
                    gltfLoader.setDRACOLoader(dracoLoaderForGLTF);
                    gltfLoader.load(resource.source, onLoad, undefined, onError);
                    break;

                case 'fbx':
                    const fbxLoader = new FBXLoader();
                    fbxLoader.load(resource.source, onLoad, undefined, onError);
                    break;

                case 'json':
                    if (originalResource.type === 'font') {
                        const fontLoader = new FontLoader();
                        fontLoader.load(resource.source, onLoad, undefined, onError);
                    } else {
                        const objectLoader = new ObjectLoader();
                        objectLoader.load(resource.source, onLoad, undefined, onError);
                    }
                    break;

                default:
                    reject(new Error(`Unsupported extension: ${extension}`));
            }
        });
    }

    // Główna metoda asynchroniczna
    public async loadAsync(_resources: any[] = []): Promise<{ [key: string]: any }> {
        if (_resources.length === 0) {
            return this.items;
        }

        // Reset liczników
        this.toLoad = _resources.length;
        this.loaded = 0;

        // Rozdziel zasoby na niezależne i zależne
        const independentResources = _resources.filter((r) => !r.dependsOn);
        const dependentResources = _resources.filter((r) => r.dependsOn);

        try {
            // 1. Ładuj wszystkie niezależne zasoby równolegle
            if (independentResources.length > 0) {
                await Promise.all(
                    independentResources.map((resource) =>
                        this.loadSingleResourceAsync(resource)
                    )
                );
            }

            // 2. Ładuj zasoby zależne w odpowiedniej kolejności
            if (dependentResources.length > 0) {
                await this.loadDependentResourcesAsync(dependentResources);
            }

            // Event 'end' zostanie wyemitowany automatycznie przez fileLoadEnd()
            // gdy this.loaded === this.toLoad

            return this.items;
        } catch (error) {
            throw new Error(`Failed to load resources: ${error}`);
        }
    }

    // Ładowanie zasobów zależnych w odpowiedniej kolejności
    private async loadDependentResourcesAsync(dependentResources: any[]): Promise<void> {
        const remaining = [...dependentResources];
        const maxIterations = remaining.length * 2; // Zabezpieczenie przed nieskończoną pętlą
        let iterations = 0;

        while (remaining.length > 0 && iterations < maxIterations) {
            iterations++;
            const currentBatch = [];

            // Znajdź zasoby, których dependencje są już załadowane
            for (let i = remaining.length - 1; i >= 0; i--) {
                const resource = remaining[i];
                const dependency = resource.dependsOn?.resource;

                if (dependency && this.items[dependency]) {
                    currentBatch.push(resource);
                    remaining.splice(i, 1);
                }
            }

            if (currentBatch.length === 0) {
                // Jeśli żaden zasób nie może być załadowany, znaczy że mamy cykliczną zależność
                // lub brakującą zależność
                const missingDeps = remaining.map(r => r.dependsOn?.resource).filter(Boolean);
                throw new Error(`Cannot resolve dependencies. Missing: ${missingDeps.join(', ')}`);
            }

            // Ładuj obecną partię równolegle
            await Promise.all(
                currentBatch.map((resource) =>
                    this.loadSingleResourceAsync(resource)
                )
            );
        }

        if (remaining.length > 0) {
            throw new Error(`Failed to load all dependent resources after ${maxIterations} iterations`);
        }
    }

    public load(_resources: any[] = []) {
        const independentResources = _resources.filter((r) => !r.dependsOn);
        const dependentResources = _resources.filter((r) => r.dependsOn);

        independentResources.forEach((_resource) => {
            this.loadSingleResource(_resource);
        });

        this.on('fileEnd', (_resource: any) => {
            dependentResources.forEach((depResource) => {
                if (depResource.dependsOn.resource === _resource.name) {
                    this.loadSingleResource(depResource);
                }
            });
        });
    }
}